/**
 * Small per-browser values. localStorage is wrapped because it throws in
 * private-mode Safari and in sandboxed iframes, and a share link that crashes
 * because a viewer has cookies locked down would be embarrassing.
 */

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Non-fatal by design: the feature degrades, the page does not break.
  }
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const ANON_ID_KEY = 'slate.anonId';
const VIEWER_NAME_KEY = 'slate.viewerName';
const SUBMITTER_NAME_KEY = 'slate.submitterName';
const MIC_ENABLED_KEY = 'slate.micEnabled';
const MIC_DEVICE_KEY = 'slate.micDevice';
const CAMERA_ENABLED_KEY = 'slate.cameraEnabled';
const CAMERA_DEVICE_KEY = 'slate.cameraDevice';

/**
 * A stable per-browser id for view beacons, so repeated visits from the same
 * browser collapse into one view row instead of inflating the count.
 * Falls back to a per-session id when storage is unavailable.
 */
let memoryAnonId: string | null = null;

export function anonId(): string {
  const existing = read(ANON_ID_KEY);
  if (existing) return existing;
  if (!memoryAnonId) memoryAnonId = randomId();
  write(ANON_ID_KEY, memoryAnonId);
  return memoryAnonId;
}

/** The display name an anonymous commenter last used. */
export const viewerName = {
  get: (): string => read(VIEWER_NAME_KEY) ?? '',
  set: (value: string): void => write(VIEWER_NAME_KEY, value),
};

/** The name an outsider gave on a request link, so repeat sends don't re-ask. */
export const submitterName = {
  get: (): string => read(SUBMITTER_NAME_KEY) ?? '',
  set: (value: string): void => write(SUBMITTER_NAME_KEY, value),
};

/**
 * Whether the recorder should bring an input up, and which physical device to
 * ask for when it does.
 *
 * `deviceId` is a hint rather than a promise. It is only written when somebody
 * picked a device by name, so that a returning user gets their headset back
 * instead of whatever the operating system currently calls default; the
 * recorder falls back to the default when the remembered id no longer resolves,
 * because the headset is quite often in the other bag.
 */
export interface InputPreference {
  enabled: boolean;
  deviceId: string | null;
}

export interface InputPreferenceStore {
  get: () => InputPreference;
  set: (value: InputPreference) => void;
}

function inputPreference(enabledKey: string, deviceKey: string): InputPreferenceStore {
  return {
    // Anything that is not an explicit "off" is on. A first-time visitor and a
    // returning one who never touched the toggle then behave identically, and
    // the default is on because a screen recording nobody can hear is the
    // mistake people only notice after they have finished talking.
    get: () => ({
      enabled: read(enabledKey) !== '0',
      deviceId: read(deviceKey) || null,
    }),
    set: (value) => {
      write(enabledKey, value.enabled ? '1' : '0');
      write(deviceKey, value.deviceId ?? '');
    },
  };
}

export const micPreference = inputPreference(MIC_ENABLED_KEY, MIC_DEVICE_KEY);
export const cameraPreference = inputPreference(CAMERA_ENABLED_KEY, CAMERA_DEVICE_KEY);

const THEME_KEY = 'slate.theme';

/** How the app is lit: the OS's way, or one of the two regardless. */
export type ThemePreference = 'system' | 'light' | 'dark';

/**
 * Read by index.html before React mounts, so the first paint is already the
 * right color; keep the key and the values in step with the inline script
 * there. Anything unrecognised is "system", which is also what a first-time
 * visitor gets.
 */
export const themePreference = {
  key: THEME_KEY,
  get: (): ThemePreference => {
    const value = read(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  },
  set: (value: ThemePreference) => write(THEME_KEY, value),
};

const TAKE_VIEW_KEY = 'slate.takeView';

/** How a take is shown while it runs: the full cover, or a strip in the corner. */
export type TakeView = 'full' | 'compact';

/**
 * Minimizing the recorder is remembered: somebody who folded it once wanted
 * the page under it, and will want it again next take.
 */
export const takeViewPreference = {
  get: (): TakeView => (read(TAKE_VIEW_KEY) === 'compact' ? 'compact' : 'full'),
  set: (value: TakeView) => write(TAKE_VIEW_KEY, value),
};

const SOUND_EFFECTS_KEY = 'slate.soundEffects';

/**
 * The countdown bells and the chime when a take is saved. On unless turned
 * off: the sounds are how somebody looking at the thing they are recording,
 * rather than at Slate, knows when the take has begun and that it was kept.
 */
export const soundEffectsPreference = {
  get: (): boolean => read(SOUND_EFFECTS_KEY) !== 'off',
  set: (on: boolean) => write(SOUND_EFFECTS_KEY, on ? 'on' : 'off'),
};

/** The proof-of-email tokens kept per recording; see queries/share.ts. */
const VIEWER_TOKEN_PREFIX = 'slate.viewer.';

/**
 * Everything here that belongs to a person rather than to the browser,
 * dropped when the person changes.
 *
 * A viewer token is proof that somebody gave their email to watch a gated
 * recording, so leaving it behind hands the next person at this browser a
 * way past that gate. The names are theirs too, and would otherwise be
 * typed into somebody else's comment. The anonymous id, the recorder's
 * device preferences, the take view and the theme stay: one identifies the
 * browser on purpose, the others are preferences about this machine and
 * this screen.
 */
export function forgetPersonalStorage(): void {
  try {
    const personal = Object.keys(window.localStorage).filter(
      (key) => key === VIEWER_NAME_KEY || key === SUBMITTER_NAME_KEY || key.startsWith(VIEWER_TOKEN_PREFIX),
    );
    for (const key of personal) window.localStorage.removeItem(key);
  } catch {
    // Same reason read and write are wrapped: storage can simply refuse.
  }
}

